import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from '../store/Store'
import { IFilterDto, TSort } from '../../common/model/Core.model'
import { contactApi } from './Contact.api'
import { IContactDto } from './Contact.model'
import { signOut } from '../auth/Auth.slice'

type InitialState = {
  filtersSelection: IFilterDto[]
  contacts: {
    error?: string
    loading: boolean
    data: IContactDto[]
    pagination?: {
      limit?: number
      sort?: TSort
      page?: string | undefined
    }
    filters?: IFilterDto[]
  }
  contact: {
    error?: string
    loading: boolean
    data?: IContactDto
  }
}

const initialState = {
  filtersSelection: [],
  contacts: {
    error: undefined,
    loading: false,
    data: [],
    pagination: {
      limit: 25,
      sort: 'ASC',
      page: undefined,
    },
    filters: undefined,
  },
  contact: {
    error: undefined,
    loading: false,
    data: undefined,
  },
} as InitialState

export const contactSlice = createSlice({
  name: 'contact',
  initialState,
  reducers: {
    setContactFiltersSelection: (state, action: PayloadAction<IFilterDto[]>) => {
      state.filtersSelection = action.payload
    },
  },
  extraReducers(builder) {
      builder
        .addCase(signOut, () => initialState)
        //Search
        .addMatcher(contactApi.endpoints.contactSearch.matchPending, (state) => {
          return {
            ...state,
            contacts: {
              ...initialState.contacts,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactSearch.matchFulfilled, (state, action) => {
          return {
            ...state,
            contacts: {
              ...state.contacts,
              error: undefined,
              loading: false,
              data: action.payload.data,
              pagination: action.payload.pagination,
              filters: action.payload.filters,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactSearch.matchRejected, (state, action) => {
          return {
            ...state,
            contacts: {
              ...state.contacts,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //LoadMore
        .addMatcher(contactApi.endpoints.contactLoadMore.matchPending, (state) => {
          return {
            ...state,
            contacts: {
              ...state.contacts,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactLoadMore.matchFulfilled, (state, action) => {
          return {
            ...state,
            contacts: {
              ...state.contacts,
              error: undefined,
              loading: false,
              data: state.contacts.data.concat(action.payload.data),
              pagination: action.payload.pagination,
              filters: action.payload.filters,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactLoadMore.matchRejected, (state, action) => {
          return {
            ...state,
            contacts: {
              ...state.contacts,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //GetById
        .addMatcher(contactApi.endpoints.contactGetById.matchPending, (state) => {
          return {
            ...state,
            contact: {
              ...initialState.contact,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactGetById.matchFulfilled, (state, action) => {
          return {
            ...state,
            contact: {
              ...state.contact,
              error: undefined,
              loading: false,
              data: action.payload,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactGetById.matchRejected, (state, action) => {
          return {
            ...state,
            contact: {
              ...state.contact,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //Create
        .addMatcher(contactApi.endpoints.contactCreate.matchPending, (state) => {
          state.contact = {
            ...initialState.contact,
            error: undefined,
            loading: true,
          }
        })
        .addMatcher(contactApi.endpoints.contactCreate.matchFulfilled, (state, action) => {
          state.contact = {
            error: undefined,
            loading: false,
            data: action.payload,
          }
        })
        .addMatcher(contactApi.endpoints.contactCreate.matchRejected, (state, action) => {
          state.contact = {
            ...initialState.contact,
            error: action.error.message,
            loading: false,
          }
        })
        //DeleteById
        .addMatcher(contactApi.endpoints.contactDeleteById.matchPending, (state) => {
          return {
            ...state,
            contact: {
              ...initialState.contact,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactDeleteById.matchFulfilled, (state, action) => {
          return {
            ...state,
            contact: {
              ...state.contact,
              error: undefined,
              loading: false,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactDeleteById.matchRejected, (state, action) => {
          return {
            ...state,
            contact: {
              ...state.contact,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //UpdateById
        .addMatcher(contactApi.endpoints.contactUpdateById.matchPending, (state) => {
          return {
            ...state,
            contact: {
              ...state.contact,
              error: undefined,
              loading: true
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactUpdateById.matchFulfilled, (state, action) => {
          return {
            ...state,
            contact: {
              ...state.contact,
              error: undefined,
              loading: false,
              data: action.payload,
            },
          }
        })
        .addMatcher(contactApi.endpoints.contactUpdateById.matchRejected, (state, action) => {
          return {
            ...state,
            contact: {
              ...state.contact,
              error: action.error.message,
              loading: false,
            },
          }
        })
  },
})

export const {setContactFiltersSelection} = contactSlice.actions
export default contactSlice.reducer

export const selectContacts = (state: RootState) => state.contactReducer.contacts.data
export const selectContactsLoading = (state: RootState) => state.contactReducer.contacts.loading
export const selectContactsPagination = (state: RootState) => state.contactReducer.contacts.pagination
export const selectContactsFilters = (state: RootState) => state.contactReducer.contacts.filters
export const selectContactFiltersSelection = (state: RootState) => state.contactReducer.filtersSelection

export const selectContact = (state: RootState) => state.contactReducer.contact.data
export const selectContactLoading = (state: RootState) => state.contactReducer.contact.loading
